import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// FETCH USERS
export const fetchAllCustomers = createAsyncThunk(
  "customers/getAll",
  async (params, { signal, rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/customers", {
        params,
        signal,
      });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch customers",
      );
    }
  },
);

// FETCH CUSTOMER STATS (KPI cards, type tab counts, quick view counts)
export const fetchCustomerStats = createAsyncThunk(
  "customers/fetchStats",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/customers/stats", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch customer stats",
      );
    }
  },
);

// FETCH CUSTOMER COMBINED SETTINGS
export const fetchCustomerCombinedSettings = createAsyncThunk(
  "customers/combinedSettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/customers/combined-settings");
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch combined settings"
      );
    }
  }
);

// FETCH CUSTOMER LIST (MINIMAL FOR DROPDOWNS)
export const fetchCustomerList = createAsyncThunk(
  "customers/getCustomerList",
  async (params, { signal, rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/customers/customer-list", {
        params,
        signal,
      });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch customer list",
      );
    }
  },
);

// CREATE USER
export const addCustomer = createAsyncThunk(
  "customers/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/customers", payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// FETCH SINGLE CUSTOMER
export const getCustomerById = createAsyncThunk(
  "customers/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/customers/${id}`);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch customer",
      );
    }
  },
);

// UPDATE CUSTOMER
export const updateCustomer = createAsyncThunk(
  "customers/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/customers/${id}`, payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// DELETE CUSTOMER BY ID
export const deleteCustomer = createAsyncThunk(
  "customers/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/customers/${id}`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to delete customer",
      );
    }
  },
);

// CHANGE CUSTOMER STATUS
export const changeCustomerStatus = createAsyncThunk(
  "customers/changeStatus",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `/tenant/customers/${id}/change-status`,
      );
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to change status");
    }
  },
);

// BULK DELETE CUSTOMERS
export const bulkDeleteCustomers = createAsyncThunk(
  "customers/bulkDelete",
  async (customerIds, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/customers/bulk-delete`, {
        data: { ids: customerIds },
      });
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// CUSTOMER NOTES
export const addCustomerNote = createAsyncThunk(
  "customers/addNote",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/customer-notes", payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to add note");
    }
  },
);

export const getCustomerNotes = createAsyncThunk(
  "customers/getNotes",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/customer-notes", { params });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to get notes");
    }
  },
);

export const updateCustomerNote = createAsyncThunk(
  "customers/updateNote",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/customer-notes/${id}`, payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to update note");
    }
  },
);

export const deleteCustomerNote = createAsyncThunk(
  "customers/deleteNote",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/customer-notes/${id}`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to delete note");
    }
  },
);

// PATCH UPDATE CUSTOMER
export const patchUpdateCustomer = createAsyncThunk(
  "customers/patchUpdate",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.patch(`/tenant/customers/${id}`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to update customer",
      );
    }
  },
);

// ADDRESS MANAGEMENT
export const addCustomerAddress = createAsyncThunk(
  "customers/addAddress",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.post(
        `/tenant/customers/${id}/add-address`,
        payload,
      );
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to add address");
    }
  },
);

export const updateCustomerAddress = createAsyncThunk(
  "customers/updateAddress",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(
        `/tenant/customers/${id}/update-address`,
        payload,
      );
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to update address");
    }
  },
);

export const deleteCustomerAddress = createAsyncThunk(
  "customers/deleteAddress",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.delete(`/tenant/customers/${id}/delete-address`, {
        data: payload,
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to delete address");
    }
  },
);
const customersSlice = createSlice({
  name: "customers",
  initialState: {
    customers: [],
    customerList: [],
    selectedCustomer: null,
    total_records: 0,
    isLoading: false,
    customerLoading: false,
    error: null,
    isBulkDeleting: false,
    currentRequestId: null,
    combinedSettings: null,
    combinedSettingsLoading: false,
    stats: null,
    isStatsLoading: false,
  },

  extraReducers: (builder) => {
    builder
      // CUSTOMER STATS
      .addCase(fetchCustomerStats.pending, (state) => {
        state.isStatsLoading = true;
      })
      .addCase(fetchCustomerStats.fulfilled, (state, action) => {
        state.isStatsLoading = false;
        state.stats = action.payload || state.stats;
      })
      .addCase(fetchCustomerStats.rejected, (state) => {
        state.isStatsLoading = false;
      })

      //GET ALL CUSTOMERS
      .addCase(fetchAllCustomers.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchAllCustomers.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isLoading = false;
        state.customers = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchAllCustomers.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch customers";
      })

      //GET MINIMAL CUSTOMER LIST 
      .addCase(fetchCustomerList.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchCustomerList.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isLoading = false;
        state.customerList = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchCustomerList.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch customer list";
      })

      // GET SINGLE CUSTOMER
      .addCase(getCustomerById.pending, (state) => {
        state.customerLoading = true;
        state.selectedCustomer = null;
      })
      .addCase(getCustomerById.fulfilled, (state, action) => {
        state.customerLoading = false;
        state.selectedCustomer = action.payload;
      })
      .addCase(getCustomerById.rejected, (state, action) => {
        state.customerLoading = false;
        state.error = action.payload || "Failed to fetch customer";
      })
      
      // FETCH COMBINED SETTINGS
      .addCase(fetchCustomerCombinedSettings.pending, (state) => {
        state.combinedSettingsLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerCombinedSettings.fulfilled, (state, action) => {
        state.combinedSettingsLoading = false;
        state.combinedSettings = action.payload;
      })
      .addCase(fetchCustomerCombinedSettings.rejected, (state, action) => {
        state.combinedSettingsLoading = false;
        state.error = action.payload || "Failed to fetch combined settings";
      });
  },
});

export default customersSlice.reducer;
